// src/store/index.js
import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";

// mockData centralizado
import { mockData } from "@/data/mockData.js";
import filters from "./modules/filters";

Vue.use(Vuex);

// URL backend Django
export const URL_API = process.env.VUE_APP_API_URL || "https://api.v3.agro.datari.site";

export default new Vuex.Store({
  modules: { filters },

  state: {
    status: null,
    token: localStorage.getItem("token") || "",
    user: {
      username: localStorage.getItem("username") || "",
      pk: localStorage.getItem("user_id") || "",
      is_staff: localStorage.getItem("user_role") === "admin",
      is_superuser: localStorage.getItem("user_superuser") === "si",
    },

    generalData: {},

    selectedYear: Number(localStorage.getItem("selectedYear")) || 2024,
    selectedProducer: localStorage.getItem("selectedProducer") || null,
    producers: [],

    allowedYears: JSON.parse(localStorage.getItem("allowedYears") || "[]"),
  },

  mutations: {
    auth_request(state) {
      state.status = "loading";
    },
    auth_success(state, payload) {
      state.status = "success";
      state.token = payload.token;
      state.user = payload.user;
    },
    auth_error(state) {
      state.status = "error";
    },
    logout(state) {
      state.status = "";
      state.token = "";
      state.user = {};
      state.generalData = {};
      state.selectedProducer = null;
    },

    setGeneralData(state, data) {
      state.generalData = data || {};
    },

    setSelectedYear(state, year) {
      state.selectedYear = Number(year);
      localStorage.setItem("selectedYear", String(year));
    },

    setProducersByYear(state, list) {
      const safe = Array.isArray(list) ? list : [];
      state.producers = safe.map(p => ({
        id: String(p.id),        // id anual
        userPk: String(p.userPk),// pk real del usuario
        nombre: p.nombre ?? String(p.id),
      }));
    },

    setSelectedProducer(state, producerId) {
      state.selectedProducer = producerId ? String(producerId) : null;
      if (producerId) {
        localStorage.setItem("selectedProducer", producerId);
      } else {
        localStorage.removeItem("selectedProducer");
      }
    },

    setAllowedYears(state, years) {
      state.allowedYears = years || [];
    },
  },

  actions: {
    login({ commit, dispatch }, credentials) {
      return new Promise((resolve, reject) => {
        commit("auth_request");

        axios
          .post(`${URL_API}/auth/login/`, credentials)
          .then((res) => {
            const token = res.data.key;

            axios.defaults.headers.common["Authorization"] = `Token ${token}`;
            localStorage.setItem("token", token);

            // Obtener datos del usuario autenticado
            axios
              .get(`${URL_API}/auth/user/`)
              .then((userRes) => {
                const user = userRes.data;

                localStorage.setItem("username", user.username);
                localStorage.setItem("user_id", user.pk);
                localStorage.setItem("user_role", user.is_staff ? "admin" : "user");
                localStorage.setItem("user_superuser", user.is_superuser ? "si" : "no");

                commit("auth_success", { token, user });

                // =========================
                //  ⭐ CALCULAR allowedYears
                // =========================
                const userPk = String(user.pk);

                let allowedYears = [];

                // ADMIN → ve todos los años
                if (user.is_staff) {
                  allowedYears = Object.keys(mockData)
                    .map(Number)
                    .sort();
                } else {
                  // PRODUCTOR → filtra por userPk
                  allowedYears = Object.keys(mockData)
                    .map(Number)
                    .filter(year => {
                      const productores = mockData?.[year]?.productoresIndex || [];
                      return productores.some(
                        p => String(p.userPk) === userPk
                      );
                    })
                    .sort();
                }

                commit("setAllowedYears", allowedYears);
                localStorage.setItem("allowedYears", JSON.stringify(allowedYears));

                // =========================
                // ⭐ FIX → Año inicial correcto
                // =========================
                const initialYear =
                  Number(localStorage.getItem("selectedYear")) || allowedYears[0];

                commit("setSelectedYear", initialYear);
                dispatch("hydrateProducersByYear", initialYear);

                resolve(res);
              })
              .catch((err) => reject(err));
          })
          .catch((err) => {
            commit("auth_error");
            localStorage.clear();
            delete axios.defaults.headers.common["Authorization"];
            reject(err);
          });
      });
    },

    logout({ commit }) {
      return new Promise((resolve) => {
        commit("logout");
        localStorage.clear();
        resolve();
      });
    },

    async fetchGeneralData({ commit, state }, producerId) {
      try {
        if (state.token) {
          axios.defaults.headers.common["Authorization"] = "Token " + state.token;
        }
        commit("setGeneralData", {});

        const r = await axios.get(`${URL_API}/api-agro/productor-detalle/${producerId}/`);
        commit("setGeneralData", r.data);

        return r.data;
      } catch (error) {
        console.error("Error al cargar datos del productor:", error);
        commit("setGeneralData", {});
        throw error;
      }
    },

    hydrateProducersByYear({ commit }, year) {
      const list = mockData?.[year]?.productoresIndex || [];
      commit("setProducersByYear", list);
    },
  },

  getters: {
    isLoggedIn: (state) => !!state.token,
    authStatus: (state) => state.status,
    get_user: (state) => state.user,

    getGeneralData: (state) => state.generalData,

    producersByYear: (state) => state.producers,

    selectedProducerObj: (state) =>
      state.producers.find((p) => p.id === state.selectedProducer) || null,

    availableYears: (state) => {
      // 1) Si NO hay productor seleccionado → admin ve todos los años
      if (!state.selectedProducer) {
        if (state.user.is_staff) {
          return Object.keys(mockData).map(Number).sort();
        }
        // productor normal usa su allowedYears
        return state.allowedYears;
      }

      // 2) Buscar el productor seleccionado en la lista ya cargada
      const prod = state.producers.find(
        p => String(p.id) === String(state.selectedProducer)
      );

      if (!prod) {
        // fallback: ver todo si algo falla
        return Object.keys(mockData).map(Number).sort();
      }

      const userPk = String(prod.userPk); // ← este es el pk REAL del productor

      // 3) Filtrar años en que ese productor existe
      return Object.keys(mockData)
        .map(Number)
        .filter(year => {
          const productores = mockData?.[year]?.productoresIndex || [];
          return productores.some(
            p => String(p.userPk) === userPk
          );
        })
        .sort();
    },


  },
});
